'use client';

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';

export type ChatSidebarView = 'list' | 'chat' | 'new' | 'draft';

export interface DraftChat {
  participantUsername: string;
  prefillText: string;
}

interface ChatSidebarState {
  isOpen: boolean;
  activeChatId: string | null;
  view: ChatSidebarView;
  width: number;
  newChatUsername: string | null;
  draftChat: DraftChat | null;
  hydrated: boolean;
}

interface ChatSidebarContextValue extends ChatSidebarState {
  toggle: () => void;
  open: (chatId?: string) => void;
  openNewChat: (username?: string) => void;
  openDraftChat: (opts: DraftChat) => void;
  close: () => void;
  setWidth: (width: number) => void;
  setView: (view: ChatSidebarView) => void;
  setActiveChatId: (chatId: string | null) => void;
}

const ChatSidebarContext = createContext<ChatSidebarContextValue | null>(null);

const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 320;
const MAX_WIDTH = 700;
const STORAGE_KEY_WIDTH = 'chat-sidebar-width';
const STORAGE_KEY_OPEN = 'chat-sidebar-open';
const STORAGE_KEY_VIEW = 'chat-sidebar-view';
const STORAGE_KEY_CHAT = 'chat-sidebar-chat-id';

function persistState(isOpen: boolean, view: ChatSidebarView, chatId: string | null) {
  localStorage.setItem(STORAGE_KEY_OPEN, String(isOpen));
  localStorage.setItem(STORAGE_KEY_VIEW, isOpen ? view : 'list');
  localStorage.setItem(STORAGE_KEY_CHAT, isOpen && chatId ? chatId : '');
}

export function ChatSidebarProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ChatSidebarState>({
    isOpen: false,
    activeChatId: null,
    view: 'list',
    width: DEFAULT_WIDTH,
    newChatUsername: null,
    draftChat: null,
    hydrated: false,
  });

  const didHydrate = useRef(false);

  useEffect(() => {
    if (didHydrate.current) return;
    didHydrate.current = true;

    const savedWidth = localStorage.getItem(STORAGE_KEY_WIDTH);
    const hasExplicitPref = localStorage.getItem(STORAGE_KEY_OPEN) !== null;
    const savedOpen = hasExplicitPref
      ? localStorage.getItem(STORAGE_KEY_OPEN) === 'true'
      : false;
    const savedView = (localStorage.getItem(STORAGE_KEY_VIEW) || 'list') as ChatSidebarView;
    const savedChat = localStorage.getItem(STORAGE_KEY_CHAT) || null;

    const w = savedWidth ? parseInt(savedWidth) : DEFAULT_WIDTH;
    const validWidth = w >= MIN_WIDTH && w <= MAX_WIDTH ? w : DEFAULT_WIDTH;

    const restoredView: ChatSidebarView = savedOpen && savedView === 'chat' && savedChat ? 'chat' : 'list';
    const restoredChatId = restoredView === 'chat' ? savedChat : null;

    setState({
      isOpen: savedOpen,
      activeChatId: restoredChatId,
      view: restoredView,
      width: validWidth,
      newChatUsername: null,
      draftChat: null,
      hydrated: true,
    });
  }, []);

  const toggle = useCallback(() => {
    setState(prev => {
      const next = !prev.isOpen;
      const newState = {
        ...prev,
        isOpen: next,
        ...(!next ? { activeChatId: null, view: 'list' as const, newChatUsername: null, draftChat: null } : {}),
      };
      persistState(newState.isOpen, newState.view, newState.activeChatId);
      return newState;
    });
  }, []);

  const open = useCallback((chatId?: string) => {
    setState(prev => {
      const newState = {
        ...prev,
        isOpen: true,
        activeChatId: chatId ?? null,
        view: (chatId ? 'chat' : 'list') as ChatSidebarView,
        newChatUsername: null,
      };
      persistState(true, newState.view, newState.activeChatId);
      return newState;
    });
  }, []);

  const openNewChat = useCallback((username?: string) => {
    setState(prev => ({
      ...prev,
      isOpen: true,
      activeChatId: null,
      view: 'new',
      newChatUsername: username ?? null,
      draftChat: null,
    }));
    persistState(true, 'new', null);
  }, []);

  const openDraftChat = useCallback((opts: DraftChat) => {
    setState(prev => ({
      ...prev,
      isOpen: true,
      activeChatId: null,
      view: 'draft',
      newChatUsername: null,
      draftChat: opts,
    }));
    persistState(true, 'draft', null);
  }, []);

  const close = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false, activeChatId: null, view: 'list', newChatUsername: null, draftChat: null }));
    persistState(false, 'list', null);
  }, []);

  const setWidth = useCallback((width: number) => {
    const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
    setState(prev => ({ ...prev, width: clamped }));
    localStorage.setItem(STORAGE_KEY_WIDTH, String(clamped));
  }, []);

  const setView = useCallback((view: ChatSidebarView) => {
    setState(prev => {
      persistState(prev.isOpen, view, prev.activeChatId);
      return { ...prev, view };
    });
  }, []);

  const setActiveChatId = useCallback((chatId: string | null) => {
    setState(prev => {
      const view = chatId ? 'chat' : 'list';
      persistState(prev.isOpen, view, chatId);
      return { ...prev, activeChatId: chatId, view };
    });
  }, []);

  return (
    <ChatSidebarContext.Provider value={{
      ...state,
      toggle,
      open,
      openNewChat,
      openDraftChat,
      close,
      setWidth,
      setView,
      setActiveChatId,
    }}>
      {children}
    </ChatSidebarContext.Provider>
  );
}

export function useChatSidebar() {
  const ctx = useContext(ChatSidebarContext);
  if (!ctx) {
    throw new Error('useChatSidebar must be used within ChatSidebarProvider');
  }
  return ctx;
}

export { MIN_WIDTH, MAX_WIDTH, DEFAULT_WIDTH };
